interface SessionTimeoutProps {
  visible: boolean
  onDismiss?: () => void
}

function Step({
  num,
  title,
  children,
  last,
}: {
  num: string
  title: string
  children: React.ReactNode
  last?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 13,
        marginBottom: last ? 0 : 14,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'rgba(201,168,76,.15)',
          border: '1px solid rgba(201,168,76,.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--gold)',
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--white)',
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        {children}
      </div>
    </div>
  )
}

export default function SessionTimeout({ visible }: SessionTimeoutProps) {
  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 800,
        background: 'var(--navy)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 24px',
        animation: 'bIn .4s ease both',
      }}
    >
      {/* Orb */}
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: 22,
          background: 'rgba(255,255,255,.07)',
          border: '1.5px solid rgba(255,255,255,.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 28,
          position: 'relative',
          animation: 'wIn .7s .1s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: -10,
            borderRadius: 30,
            border: '1px solid rgba(255,255,255,.06)',
            animation: 'ring 3s ease-in-out infinite',
          }}
        />
        <span
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: 32,
            fontWeight: 500,
            color: 'rgba(201,168,76,.5)',
          }}
        >
          A
        </span>
      </div>

      {/* Badge */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(239,68,68,.12)',
          border: '1px solid rgba(239,68,68,.25)',
          borderRadius: 100,
          padding: '4px 12px',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: '#F87171',
          marginBottom: 18,
          animation: 'wIn .6s .2s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        <div
          style={{
            width: 5,
            height: 5,
            background: '#F87171',
            borderRadius: '50%',
          }}
        />
        Session Ended
      </div>

      {/* Title */}
      <h2
        style={{
          fontFamily: "'Playfair Display',serif",
          fontSize: 26,
          fontWeight: 500,
          color: 'var(--white)',
          lineHeight: 1.3,
          marginBottom: 10,
          animation: 'wIn .6s .25s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        Your session has
        <br />
        <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>timed out.</em>
      </h2>

      {/* Subtext */}
      <p
        style={{
          fontSize: 13.5,
          color: 'rgba(255,255,255,.5)',
          lineHeight: 1.7,
          maxWidth: 340,
          margin: '0 auto 36px',
          animation: 'wIn .6s .3s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        No worries — your conversation history is safe. Just log back in to pick up where you left off.
      </p>

      {/* Steps card - from REF */}
      <div
        style={{
          background: 'rgba(255,255,255,.05)',
          border: '1px solid rgba(255,255,255,.1)',
          borderRadius: 18,
          padding: '24px 28px',
          maxWidth: 400,
          width: '100%',
          margin: '0 auto 28px',
          textAlign: 'left',
          animation: 'wIn .6s .35s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--gold)',
            marginBottom: 16,
            display: 'block',
          }}
        >
          How to get back in
        </span>

        <Step num="1" title="Go to the QL One app">
          <a
            href="https://qloneapp.com"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11.5,
              color: 'var(--gold)',
              marginTop: 3,
              background: 'rgba(201,168,76,.1)',
              border: '1px solid rgba(201,168,76,.2)',
              borderRadius: 6,
              padding: '3px 9px',
              textDecoration: 'none',
            }}
          >
            ↗ qloneapp.com
          </a>
        </Step>

        <Step num="2" title="Open the My MBA section">
          <div
            style={{
              fontSize: 11.5,
              color: 'rgba(255,255,255,.45)',
              lineHeight: 1.55,
              marginTop: 2,
            }}
          >
            Find it in the main navigation once you are logged in.
          </div>
        </Step>

        <Step num="3" title="Click the Athena button" last>
          <div
            style={{
              fontSize: 11.5,
              color: 'rgba(255,255,255,.45)',
              lineHeight: 1.55,
              marginTop: 2,
            }}
          >
            That's it — you will be right back here.
          </div>
        </Step>
      </div>

      {/* CTA - from REF */}
      <a
        href="https://qloneapp.com"
        target="_blank"
        rel="noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--gold)',
          color: 'var(--navy)',
          borderRadius: 13,
          padding: '13px 28px',
          fontSize: 14,
          fontWeight: 600,
          textDecoration: 'none',
          boxShadow: '0 4px 20px rgba(201,168,76,.3)',
          transition: 'all .2s',
          animation: 'wIn .6s .4s cubic-bezier(.16,1,.3,1) both',
          opacity: 0,
        }}
      >
        Go to qloneapp.com →
      </a>
    </div>
  )
}
